"use client";

import { Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { coreCapabilities } from "@/lib/data/homepage";
import { EmotionalHeatmap } from "./visuals";

/**
 * Capabilities — Radial wheel of 6 capabilities with center
 * brand mark and side panel detailing each.
 */
export function Capabilities() {
  const accents = [
    "var(--color-crimson)",
    "var(--color-purple)",
    "var(--color-cyan)",
    "var(--color-emerald)",
    "var(--color-amber)",
    "var(--color-coral)",
  ];

  return (
    <section id="capabilities" className="relative section-pad">
      <Container>
        <Reveal className="text-center max-w-3xl mx-auto mb-12">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass eyebrow-tiny text-[var(--color-purple)]">
            <span className="h-1.5 w-1.5 rounded-full bg-[var(--color-purple)]" />
            Core Capabilities
          </span>
          <h2 className="mt-6">
            The Engine Behind Perception Dominance
          </h2>
        </Reveal>

        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-8 items-center">
          {/* LEFT — radial wheel of 6 capabilities */}
          <Reveal>
            <div className="relative aspect-square max-w-md mx-auto">
              <svg viewBox="0 0 400 400" className="w-full h-full">
                <defs>
                  <radialGradient id="wheelCore" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="rgb(220 20 60 / 0.3)" />
                    <stop offset="100%" stopColor="rgb(220 20 60 / 0)" />
                  </radialGradient>
                </defs>

                {/* Concentric guide circles */}
                <circle cx="200" cy="200" r="180" fill="none" stroke="rgb(255 255 255 / 0.05)" />
                <circle cx="200" cy="200" r="130" fill="none" stroke="rgb(255 255 255 / 0.05)" />
                <circle cx="200" cy="200" r="80" fill="none" stroke="rgb(255 255 255 / 0.05)" />

                {/* Center brand mark */}
                <circle cx="200" cy="200" r="60" fill="url(#wheelCore)" />
                <circle cx="200" cy="200" r="50" fill="rgb(10 10 15 / 0.85)" />
                <text x="200" y="198" textAnchor="middle" fontSize="24" fontWeight="900" fill="white">X2</text>
                <text x="200" y="216" textAnchor="middle" fontSize="8" fontWeight="600" fill="rgb(220 20 60)" letterSpacing="2">6 CAPABILITIES</text>

                {/* 6 capability nodes around the wheel */}
                {coreCapabilities.map((c, i) => {
                  const ROUND = (n: number) => Math.round(n * 100) / 100;
                  const angle = (i / 6) * Math.PI * 2 - Math.PI / 2;
                  const x = ROUND(200 + Math.cos(angle) * 155);
                  const y = ROUND(200 + Math.sin(angle) * 155);
                  return (
                    <g key={c.title}>
                      {/* Connection line to center */}
                      <line
                        x1="200"
                        y1="200"
                        x2={x}
                        y2={y}
                        stroke={accents[i]}
                        strokeWidth="1"
                        strokeOpacity="0.3"
                        strokeDasharray="3 3"
                      />
                      {/* Node */}
                      <circle cx={x} cy={y} r="40" fill="rgb(10 10 15 / 0.6)" />
                      <circle cx={x} cy={y} r="38" fill={`color-mix(in oklch, ${accents[i]} 18%, transparent)`} stroke={accents[i]} strokeWidth="1.5" />
                      <text x={x} y={y - 4} textAnchor="middle" fontSize="7" fontWeight="700" fill="white" letterSpacing="0.5">
                        {c.title.toUpperCase().split(" ").slice(0, 2).join(" ")}
                      </text>
                      <text x={x} y={y + 8} textAnchor="middle" fontSize="5" fill="rgb(255 255 255 / 0.5)">
                        {c.metric}
                      </text>
                    </g>
                  );
                })}
              </svg>
            </div>
          </Reveal>

          {/* RIGHT — detailed capability cards (stacked, varied) */}
          <Reveal delay={120}>
            <div className="space-y-3">
              {coreCapabilities.map((c, i) => (
                <div
                  key={c.title}
                  className="group rounded-2xl border border-[rgb(var(--border)/0.18)] bg-[rgb(var(--surface)/0.5)] hover:bg-[rgb(var(--surface)/0.8)] p-4 flex items-start gap-4 transition-all"
                >
                  <div
                    className="shrink-0 grid place-items-center h-10 w-10 rounded-lg font-mono-num font-bold text-sm"
                    style={{
                      background: `color-mix(in oklch, ${accents[i]} 22%, transparent)`,
                      color: accents[i],
                      boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accents[i]} 40%, transparent)`,
                    }}
                  >
                    0{i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3">
                      <h3>{c.title}</h3>
                      <span
                        className="eyebrow-tiny px-2 py-0.5 rounded-full shrink-0"
                        style={{
                          background: `color-mix(in oklch, ${accents[i]} 18%, transparent)`,
                          color: accents[i],
                        }}
                      >
                        {c.metric}
                      </span>
                    </div>
                    <p className="mt-1.5 text-body-xs leading-relaxed text-[rgb(var(--fg-muted))]">{c.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </Container>
    </section>
  );
}
